import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import i18n from 'i18next';
import {Link} from '@inertiajs/inertia-react';
import '@inertiajs/inertia-react';
import MyGivingIcon from '@/../icons/my-giving.svg';
import ModuleCard from "@/Components/ModuleCard";
import {ModuleTrialCard} from "@/Components/ModuleTrialCard";
import UpgradeTodayModal from "@/Components/Modals/UpgradeTodayModal";
import ProductLogo from "@/Components/ProductLogo";

export default function WelcomeAdmin({ userData, features, salesloftURL }) {
    const firstName = userData ? userData.first_name : "";
    const modules = userData ? userData.modules : [];
    const showMyGiving = userData ? userData.current_organization.my_giving?.enabled : false;
    const showMyChurchProfile = userData ? userData.current_organization.my_church_profile?.enabled : false;
    const haveMyAppCards = Boolean(showMyGiving || showMyChurchProfile ||
        modules.filter(module => !module.trial && module.dashboard_display === "as_my_app_card").length);
    const userIsAdmin = userData ? userData.current_organization?.user_is_admin : false;
    const hasCustomSalesForceOffer = userData ? userData.current_organization?.has_custom_salesforce_offer : false;
    const trialCount = userData ? userData.current_organization?.trial_count : 0;
    const failedPayment = userData ? userData.current_organization?.failed_payment : false;
    const welcomeHeaderRef = useRef(null);

    const [showTrialModal, setShowTrialModal] = useState(false);

    const toggleTrialModal = () => {
        setShowTrialModal(s => !s);
    }

    const goToModule = (url) => {
        window.location.href = url;
    }

    useLayoutEffect(() => {
        const welcomeAdminDiv = document.querySelector('.welcome-admin');

        let marginTopCount = 60;

        if((hasCustomSalesForceOffer || (userIsAdmin && trialCount > 0 && features['trials']))
            && features['checkout']) {
            marginTopCount += 30;
        }

        if(failedPayment){
            marginTopCount += 30;
        }

        welcomeAdminDiv.style.marginTop = `${marginTopCount}px`;
    },[])

    const adjustNameSize = function() {
        const welcomeTextElement = welcomeHeaderRef.current;
        const textWidth = welcomeTextElement.offsetWidth;
        //Width of the header container or half the current browser window size, whichever is smallest
        const maxWidth = Math.min(550, window.innerWidth*0.5);

        //Checks if the width of the Welcome User text is bigger than the max allowed width
        if(textWidth > maxWidth){
            //Calculates a scaling factor to determine what size the text should be to stay within the max width
            let scalingFactor = maxWidth/textWidth;

            let x = Math.pow(scalingFactor, 0.6);
            let y = Math.pow(scalingFactor, 0.25);

            //Scales the welcome text to fit within the max width and centers it on the screen
            welcomeTextElement.style = "transform: translate(-50%) scale(" + x + "," + y + ")";
        }
        else{
            welcomeTextElement.style = "";
        }
    }

    window.onresize = adjustNameSize;

    useEffect(() => {
        adjustNameSize();
    }, [firstName]);

    return (
        <div className="welcome-admin">
            <h1>
                <div ref={welcomeHeaderRef} id="welcome-header-text">
                    {i18n.t("welcome-admin.welcome")}, {firstName}!
                </div>
            </h1>
            <div className="row modules">
                {modules.filter(module => module.trial).map((module) => {
                        let dataTestId = 'welcome_admin-div-trial_module'
                        if (module.trial.days_remaining <= 0){
                            dataTestId = 'welcome_admin-div-trial-expired_module';
                        }
                        return(
                            <div key={module.uuid} className="col-12 col-sm-6 col-md-6 mb-4"
                                 data-testid={dataTestId}>
                                <ModuleTrialCard module={module}
                                                 userIsAdmin={userIsAdmin}
                                                 hasCustomSalesForceOffer={hasCustomSalesForceOffer}
                                                 salesloftURL={salesloftURL}
                                                 features={features}
                                                 useModuleSalesName={false}
                                />
                            </div>
                        )
                    }
                )}

                <UpgradeTodayModal show={showTrialModal} onHide={toggleTrialModal}></UpgradeTodayModal>

                {modules.filter(module => !module.trial && module.dashboard_display === "as_module_card"
                    && (module.navigable || module.provisioning)).map((module) =>
                    <div onClick={() => !module.provisioning && goToModule(module.access_url)} key={module.uuid} provisioning={module.provisioning.toString()}
                         className="col-6 col-sm-3 col-md-3 mb-4" data-testid={'welcome_admin-div-active_module'} style={{textAlign:"right"}}>

                        {module.provisioning &&
                        <span style={{position:"absolute"}}>
                            <img width="17px" height="17px" src={"images/info_circle.svg"} alt={"info"}
                                 style={{zIndex: "2", opacity:0.5, position:"relative", right:"26px", top:"2px"}} />
                            <span id={"provisioning_tooltip"}>{i18n.t("module-trial-card.provisioning_tool_tip")}</span>
                        </span>
                        }
                        <ModuleCard
                            module={module}
                            interactive={false}
                            hoverable={!module.provisioning}
                            useModuleBackgroundHoverColor={true}
                         ></ModuleCard>
                    </div>
                )}
                {userIsAdmin &&
                <div className="col-6 col-sm-3 col-md-3 mb-4">
                    <Link data-testid="welcome_admin-link-manage" href={route('manage.account-overview')} className="no-link">
                        <div className="manage-modules">
                            <div className='manage-module'>
                                <div className='manage-module-flex'>
                                    <img className='manage' src='/images/manage-cogs.svg' ></img>
                                    <div className="module-title">
                                        {i18n.t("welcome-admin.manage")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Link>
                </div>
                }
            </div>
            {haveMyAppCards &&
                <div className="row">
                    <div className="col-12">
                        <hr></hr>
                    </div>
                </div>
            }
            <div className="row">
                {showMyChurchProfile &&
                    <div className="col-12 col-sm-6 my-app-container">
                        <div className="my-app-card"
                             onClick={() => goToModule(userData.current_organization.my_church_profile.url)}
                             key={module.uuid}>
                            <div className="user-avatar-container">
                                <div className="user-avatar">
                                    <img src={userData.avatars.md}
                                         alt={`${userData.first_name} ${userData.last_name} avatar`}/>
                                </div>
                            </div>
                            <div className="module-title">
                                {i18n.t("welcome-admin.my-church-profile")}
                            </div>
                        </div>
                    </div>
                }
                {showMyGiving &&
                    <div className="col-12 col-sm-6 my-app-container">
                        <div className="my-app-card" onClick={() => goToModule(userData.current_organization.my_giving.url)}>
                            <div className="logo">
                                <img alt="My Giving" src={MyGivingIcon}/>
                            </div>
                            <div className="module-title">
                                {i18n.t("welcome-admin.my-giving")}
                            </div>
                        </div>
                    </div>
                }
                {modules.filter(module => !module.trial && module.dashboard_display === "as_my_app_card").map((module) =>
                    <div className="col-12 col-sm-6 my-app-container" key={module.uuid}>
                        <div className="my-app-card" onClick={() => goToModule(module.access_url)}>
                            <div className="logo">
                                 <span
                                     style={{color: module.colors.border}}
                                     dangerouslySetInnerHTML={{__html: module.tile}}
                                 ></span>
                            </div>
                            <div className="module-title">
                                {module.name}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="row">
                <div className="discover-container">
                    <div data-testid="welcome_admin-div-discover_text" className="discover-text">
                        {i18n.t("welcome-admin.discover")}
                    </div>
                    <a href="https://www.ministrybrands.com/ebooks/state-of-church-giving-2025">
                        <div className="discover-banner-container">
                            <img data-testid="welcome_admin-img-discover_banner" id="discover-banner" src="images/socg_2025.png" />
                        </div>
                    </a>
                </div>
            </div>
            <div className="row justify-content-center">
                <div className="logo-container">
                    <ProductLogo />
                </div>
            </div>
            <input type="hidden" id="organization_quote_id" value={userData.current_organization.quote_id} />
        </div>
    );
}
